import type React from 'react';
// Routes inside a shop (/s/:slug/...).
import { Navigate, Route, Routes } from 'react-router';
import { Book } from '../customer/Book.tsx';
import { Info } from '../customer/Info.tsx';
import { Invite } from '../customer/Invite.tsx';
import { MyBookings, Reschedule } from '../customer/MyBookings.tsx';
import { Privacy } from '../customer/Privacy.tsx';
import { Profile } from '../customer/Profile.tsx';
import { Register } from '../customer/Register.tsx';
import { Barbers } from '../owner/Barbers.tsx';
import { Hours } from '../owner/Hours.tsx';
import { Export, Reports } from '../owner/Export.tsx';
import { GroupLink } from '../owner/GroupLink.tsx';
import { Manage } from '../owner/Manage.tsx';
import { RichMenu } from '../owner/RichMenu.tsx';
import { SettingsPage } from '../owner/Settings.tsx';
import { Setup } from '../owner/Setup.tsx';
import { ShopInfo } from '../owner/ShopInfo.tsx';
import { StaffList } from '../owner/StaffList.tsx';
import { Services } from '../owner/Services.tsx';
import { SpecialHours, TimeOff } from '../owner/TimeOff.tsx';
import { Customers } from '../staff/Customers.tsx';
import { Day } from '../staff/Day.tsx';
import { StaffBook } from '../staff/StaffBook.tsx';
import { Empty } from '../ui/index.tsx';
import { useShop } from './boot.tsx';
import { ShopHeader, useShopTheme } from './brand.tsx';

export function ShopApp() {
  const { isStaff, isOwner, shop } = useShop();
  const home = isOwner && shop.services.length === 0 ? 'staff/setup' : isStaff ? 'staff/today' : 'book';
  useShopTheme();
  return (
    <div className="app">
      <ShopHeader />
      <Routes>
        <Route index element={<Navigate to={home} replace />} />
        <Route path="book" element={<Book />} />
        <Route path="my" element={<MyBookings />} />
        <Route path="my/:id/reschedule" element={<Reschedule />} />
        <Route path="register" element={<Register />} />
        <Route path="profile" element={<Profile />} />
        <Route path="info" element={<Info />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="staff/today" element={<StaffOnly><Day mode="today" /></StaffOnly>} />
        <Route path="staff/calendar" element={<StaffOnly><Day mode="calendar" /></StaffOnly>} />
        <Route path="staff/book" element={<StaffOnly><StaffBook /></StaffOnly>} />
        <Route path="staff/customers" element={<StaffOnly><Customers /></StaffOnly>} />
        <Route path="staff/manage" element={<OwnerOnly><Manage /></OwnerOnly>} />
        <Route path="staff/manage/barbers" element={<OwnerOnly><Barbers /></OwnerOnly>} />
        <Route path="staff/manage/services" element={<OwnerOnly><Services /></OwnerOnly>} />
        <Route path="staff/manage/hours" element={<OwnerOnly><Hours /></OwnerOnly>} />
        <Route path="staff/manage/time-off" element={<StaffOnly><TimeOff /></StaffOnly>} />
        <Route path="staff/manage/special-hours" element={<OwnerOnly><SpecialHours /></OwnerOnly>} />
        <Route path="staff/manage/settings" element={<OwnerOnly><SettingsPage /></OwnerOnly>} />
        <Route path="staff/manage/shop-info" element={<OwnerOnly><ShopInfo /></OwnerOnly>} />
        <Route path="staff/manage/staff" element={<OwnerOnly><StaffList /></OwnerOnly>} />
        <Route path="staff/manage/group" element={<OwnerOnly><GroupLink /></OwnerOnly>} />
        <Route path="staff/manage/rich-menu" element={<OwnerOnly><RichMenu /></OwnerOnly>} />
        <Route path="staff/manage/reports" element={<OwnerOnly><Reports /></OwnerOnly>} />
        <Route path="staff/manage/export" element={<OwnerOnly><Export /></OwnerOnly>} />
        <Route path="staff/setup" element={<OwnerOnly><Setup /></OwnerOnly>} />
        <Route path="invite/:token" element={<Invite />} />
        <Route path="*" element={<Empty title="ไม่พบหน้านี้" />} />
      </Routes>
    </div>
  );
}

export function NoAccess() {
  return <Empty title="คุณไม่มีสิทธิ์ทำรายการนี้" text="หน้านี้สำหรับพนักงานหรือเจ้าของร้านเท่านั้น" />;
}

export function StaffOnly({ children }: { children: React.ReactNode }) {
  const { isStaff } = useShop();
  return isStaff ? <>{children}</> : <NoAccess />;
}

export function OwnerOnly({ children }: { children: React.ReactNode }) {
  const { isOwner } = useShop();
  return isOwner ? <>{children}</> : <NoAccess />;
}
